import React from 'react'
import styled from '@emotion/styled'

import { Text } from '../Text/Text'
import { useScreenSize } from '../../hooks/useScreenSize'
import useResponsiveProp from '../../hooks/useResponsiveProp'

export interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  type?: 'button' | 'submit'
  disabled?: boolean
  loading?: boolean
  fullWidth?: ResponsiveProp<boolean>
  size?: ResponsiveProp<'small' | 'base' | 'big' | 'bigger'>
}

const Button: React.FC<ButtonProps> = ({
  type = 'button',
  disabled = false,
  loading = false,
  fullWidth = false,
  size = 'base',
  ...props
}) => {
  const device = useScreenSize()

  const responsiveFullWidth = useResponsiveProp(fullWidth, device)
  const responsiveSize = useResponsiveProp(size, device)

  return (
    <button type={type} disabled={disabled} {...props}>
      <Text size={responsiveSize} weight="stronger" align={responsiveFullWidth ? 'center' : 'left'}>
        {props.children}
      </Text>
    </button>
  )
}

export const WrapperButton: React.FC<ButtonProps> = ({ disabled, children, ...props }) => (
  <button type="button" disabled={disabled} {...props} style={{ cursor: 'pointer' }}>
    {children}
  </button>
)

export const PrimaryButton = styled(Button)<ButtonProps>`
  outline: none;
  cursor: pointer;
  transition: var(--transition);
  border-radius: var(--border-radius--xxs);
  border: 2px solid var(--button-border--primary);
  background: var(--button-bg--primary);

  span {
    color: var(--button-text--primary);
  }

  padding: var(--space-inset-squish--s);
  ${(p) =>
    p.size === 'small' &&
    `
      padding: var(--space-inset-squish--xs);
    `}

  ${(p) =>
    p.size === 'bigger' &&
    `
      padding: var(--space-inset-squish--m);
    `}

  ${(p) =>
    p.disabled ||
    `
      &:hover {
        background: var(--button-hover--primary);
        border-color: var(--button-hover--primary);
      }
    `}

  ${(p) =>
    p.disabled &&
    `
      opacity: 30%;
      cursor: not-allowed;
    `}

  ${(p) =>
    p.fullWidth &&
    `
      width: 100%;
    `}
`

export const SecondaryButton = styled(PrimaryButton)<ButtonProps>`
  background: var(--button-bg--secondary);
  border: 2px solid var(--button-border--secondary);

  span {
    color: var(--button-text--secondary);
  }

  ${(p) =>
    p.disabled ||
    `
      &:hover {
        background: var(--button-hover--secondary);
      }
    `}
`

export const DestructiveButton = styled(PrimaryButton)<ButtonProps>`
  background: var(--button-bg--destructive);
  border: 2px solid var(--button-border--destructive);

  ${(p) =>
    p.disabled ||
    `
      &:hover {
        background: var(--button-hover--destructive);
        border-color: var(--button-hover--destructive);
      }
    `}
`

export const TextButton = styled(PrimaryButton)<ButtonProps>`
  background: none;
  border: 2px solid var(--button-border-text);

  span {
    color: var(--button-text--text);
  }

  ${(p) =>
    p.disabled ||
    `
      &:hover {
        background: var(--button-hover--text);
        border-color: var(--button-hover--text);
      }
    `}
`
